import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { PESTS } from "@/data/pests";

/**
 * Quick-jump selector that sits ABOVE the pest card grid — it does not replace
 * it. The cards carry the crawlable internal links to each pest page and the
 * browse experience; this is a shortcut for someone who already knows what
 * they've got and doesn't want to thumb past six cards on a phone.
 *
 * Deliberately a native <select>: mobile browsers render it as the OS picker,
 * which is far easier to operate with a thumb than a custom listbox, and it is
 * keyboard- and screen-reader-accessible for free.
 */
export default function PestJump({ variant = "light", className = "" }) {
  const navigate = useNavigate();
  const dark = variant === "dark";

  const onPick = (e) => {
    const to = e.target.value;
    if (to) navigate(to);
  };

  const label = dark ? "text-cream/70" : "text-forest-950/60";
  const field = dark
    ? "bg-cream/10 border-cream/25 text-cream focus:border-duckgreen"
    : "bg-cream border-forest/15 text-forest-950 focus:border-duckgreen";
  const chevron = dark ? "text-cream/60" : "text-forest-950/45";

  return (
    <div className={className} data-testid="pest-jump">
      <label
        htmlFor="pest-jump-select"
        className={`block text-sm font-medium mb-2 ${label}`}
      >
        Know what it is? Jump straight there.
      </label>
      <div className="relative max-w-sm">
        <select
          id="pest-jump-select"
          value=""
          onChange={onPick}
          className={`w-full appearance-none rounded-2xl border px-5 py-4 pr-12 text-base font-medium outline-none focus:ring-4 focus:ring-duckgreen/25 transition-colors cursor-pointer ${field}`}
          data-testid="pest-jump-select"
        >
          <option value="">Choose a pest…</option>
          {PESTS.map((p) => (
            <option key={p.slug} value={`/pests/${p.slug}`}>
              {p.name}
            </option>
          ))}
          <option value="/hire-a-pro">Not sure — help me identify it</option>
        </select>
        <ChevronDown
          className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 ${chevron}`}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
